import { ExecutionMethod, Functions } from "appwrite";

import { account, client } from "src/handlers/Appwrite";

const PROJECT_SYNC_FUNCTION_ID =
  import.meta.env.VITE_APPWRITE_PROJECT_SYNC_FUNCTION_ID || "project-sync";

const functions = new Functions(client);
const EXECUTION_POLL_ATTEMPTS = 5;
const EXECUTION_POLL_DELAY_MS = 250;
const EXECUTION_RETRY_ATTEMPTS = 2;

function parseExecutionResponse(execution) {
  const body = execution?.responseBody || "";

  if (!body) return null;

  try {
    return JSON.parse(body);
  } catch {
    return {
      ok: false,
      error: body
    };
  }
}

export async function executeProjectSync(xpath, payload, attempt = 0) {
  const jwt = await account.createJWT();
  let execution = await functions.createExecution({
    functionId: PROJECT_SYNC_FUNCTION_ID,
    body: JSON.stringify({
      ...(payload ?? {}),
      jwt: jwt.jwt
    }),
    async: false,
    xpath,
    method: ExecutionMethod.POST,
    headers: {
      "content-type": "application/json"
    }
  });

  for (let attempt = 0; attempt < EXECUTION_POLL_ATTEMPTS; attempt += 1) {
    if (execution?.responseBody) break;

    await new Promise((resolve) => {
      window.setTimeout(resolve, EXECUTION_POLL_DELAY_MS);
    });

    execution = await functions.getExecution({
      functionId: PROJECT_SYNC_FUNCTION_ID,
      executionId: execution.$id
    });
  }

  const response = parseExecutionResponse(execution);

  if (!response?.ok) {
    if (
      attempt + 1 < EXECUTION_RETRY_ATTEMPTS &&
      Number(execution?.responseStatusCode || 0) >= 500
    ) {
      await new Promise((resolve) => {
        window.setTimeout(resolve, EXECUTION_POLL_DELAY_MS * (attempt + 1));
      });
      return executeProjectSync(xpath, payload, attempt + 1);
    }

    throw new Error(
      response?.error ||
        execution?.responseBody ||
        `Project sync request failed (status ${execution?.responseStatusCode || "unknown"}).`
    );
  }

  return response;
}

export async function ensureSharedDeviceRow({ projectId, type, tabId, tabData }) {
  return executeProjectSync("/device/upsert", {
    projectId,
    type,
    tabId,
    tabData
  });
}

export async function syncSharedDeviceRows({ projectId, type, tabs }) {
  return executeProjectSync("/device/bulk-sync", {
    projectId,
    type,
    tabs
  });
}

export async function ensureSharedPageRow({ projectId, pageKey, data }) {
  return executeProjectSync("/page/upsert", {
    projectId,
    pageKey,
    data
  });
}

export async function deleteSharedPageRow({ projectId, pageKey }) {
  return executeProjectSync("/page/delete", {
    projectId,
    pageKey
  });
}

export async function repairProjectPermissions({ projectId }) {
  return executeProjectSync("/permissions/repair", {
    projectId
  });
}
